import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import { useAuth } from "../App";
import LogLayout from "../components/LogLayout";
import SectionTabs, { ANALYTICS_TABS } from "../components/SectionTabs";
import { BarChart3, TrendingUp, TrendingDown, Users, Award, Building2, MapPin, Globe, CalendarDays, ArrowUpRight, ArrowDownRight, Minus, Mail, Loader2, Send, Trophy, Star, Crown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid, Area, AreaChart, ComposedChart } from "recharts";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const formatFCFA = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0)) + " FCFA";
const formatShort = (n) => n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(0)}K` : n;
const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#6366F1"];

export default function LogAnalyticsPage() {
  const [data, setData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [rankingData, setRankingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(""); // "" = current month
  const [tab, setTab] = useState("monthly");
  const [sendingEmail, setSendingEmail] = useState(false);
  const { user: authUser } = useAuth();
  const userRole = authUser?.role;

  useEffect(() => {
    const load = async () => {
      try {
        const h = getHeaders();
        const [res, geoRes, monthlyRes, rankRes] = await Promise.all([
          axios.get(`${API}/log/analytics`, { headers: h }),
          axios.get(`${API}/log/analytics/geographic`, { headers: h }),
          axios.get(`${API}/log/analytics/monthly-comparative`, { headers: h }),
          axios.get(`${API}/log/analytics/pharmacy-ranking`, { headers: h }),
        ]);
        setData(res.data);
        setGeoData(geoRes.data);
        setMonthlyData(monthlyRes.data);
        setRankingData(rankRes.data);
      } catch { }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // Reload monthly data when user changes the selected month
  const handleMonthChange = async (newMonth) => {
    setSelectedMonth(newMonth);
    setMonthlyLoading(true);
    try {
      const url = newMonth
        ? `${API}/log/analytics/monthly-comparative?month=${newMonth}`
        : `${API}/log/analytics/monthly-comparative`;
      const res = await axios.get(url, { headers: getHeaders() });
      setMonthlyData(res.data);
    } catch { }
    finally { setMonthlyLoading(false); }
  };

  if (loading) return (
    <LogLayout title="Analytics & Prédictions" subtitle="Performance, benchmarking, rapports et IA">
      <SectionTabs tabs={ANALYTICS_TABS} />
      <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>
    </LogLayout>
  );

  const my = data?.my_performance || {};
  const market = data?.market_benchmark || {};
  const diff = my.recovery_rate - (market.avg_recovery_rate || 0);

  const tabs = [
    { key: "monthly", label: "Comparatif Mensuel", icon: CalendarDays },
    { key: "ranking", label: "Classement", icon: Trophy },
    { key: "performance", label: "Performance", icon: TrendingUp },
    { key: "geographic", label: "Géographique", icon: MapPin },
    { key: "insurers", label: "Assureurs", icon: Building2 },
  ];

  return (
    <LogLayout title="Analytics & Prédictions" subtitle="Performance : comparaison et benchmarking anonymisé">
      <SectionTabs tabs={ANALYTICS_TABS} />
      <div data-testid="log-analytics-page" className="space-y-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-200 shadow-sm" data-testid="analytics-tabs">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`analytics-tab-${t.key}`}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${tab === t.key ? "bg-sky-500 text-white shadow-sm" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Monthly Comparative Tab */}
        {tab === "monthly" && monthlyData && (
          <MonthlyComparativeTab
            data={monthlyData}
            userRole={userRole}
            sendingEmail={sendingEmail}
            setSendingEmail={setSendingEmail}
            selectedMonth={selectedMonth}
            onMonthChange={handleMonthChange}
            monthlyLoading={monthlyLoading}
          />
        )}

        {/* Ranking Tab - Anonymized Pharmacy Ranking */}
        {tab === "ranking" && rankingData && (
          <div className="space-y-6" data-testid="ranking-tab">
            {/* My Position Summary */}
            <div className="bg-gradient-to-r from-sky-50 to-violet-50 border border-sky-200 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-sky-500 flex items-center justify-center shadow-lg">
                    <Trophy className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-medium">Votre position</p>
                    <p className="text-3xl font-black text-slate-800">#{rankingData.my_rank} <span className="text-base font-medium text-slate-500">/ {rankingData.total_pharmacies}</span></p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Percentile</p>
                  <p className="text-2xl font-bold text-sky-600">Top {100 - rankingData.percentile}%</p>
                </div>
              </div>
            </div>

            {/* Bar Chart - Recovery Rate Ranking */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800 mb-1">Classement par taux de recouvrement</h3>
              <p className="text-[10px] text-slate-400 mb-4">Les pharmacies sont anonymisees. Seul votre nom est visible.</p>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rankingData.rankings.slice(0, 15)} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="name" width={130} tick={({ x, y, payload }) => {
                      const entry = rankingData.rankings.find(r => r.name === payload.value);
                      const isMe = entry?.is_me;
                      return (
                        <text x={x} y={y} dy={4} textAnchor="end" fill={isMe ? "#0369a1" : "#64748b"} fontSize={11} fontWeight={isMe ? 700 : 400}>
                          {isMe ? `★ ${payload.value}` : payload.value}
                        </text>
                      );
                    }} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg text-xs">
                          <p className="font-bold text-slate-800 mb-1">{d.is_me ? `★ ${d.name} (vous)` : d.name}</p>
                          <p className="text-slate-500">Taux: <span className="font-bold text-emerald-600">{d.recovery_rate}%</span></p>
                          <p className="text-slate-500">Factures: {d.total_invoices} | Payées: {d.nb_paid}</p>
                          <p className="text-slate-500">Montant: {formatFCFA(d.total_amount)}</p>
                          <p className="text-slate-500">Recouvré: {formatFCFA(d.total_paid)}</p>
                        </div>
                      );
                    }} />
                    <Bar dataKey="recovery_rate" radius={[0, 6, 6, 0]} name="Taux recouvrement (%)">
                      {rankingData.rankings.slice(0, 15).map((entry, i) => (
                        <Cell key={i} fill={entry.is_me ? "#0369a1" : entry.recovery_rate >= 50 ? "#10B981" : entry.recovery_rate >= 25 ? "#F59E0B" : "#EF4444"} 
                          stroke={entry.is_me ? "#0369a1" : "none"} strokeWidth={entry.is_me ? 2 : 0} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-4 mt-3 text-[10px] text-slate-400">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#0369a1]" /> Votre pharmacie</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" /> Bon (50%+)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500" /> Moyen (25-50%)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500" /> Faible (&lt;25%)</span>
              </div>
            </div>

            {/* Detailed Ranking Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="p-4 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> Tableau detaille</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="ranking-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left p-3 text-slate-500 font-medium w-10">#</th>
                      <th className="text-left p-3 text-slate-500 font-medium">Pharmacie</th>
                      <th className="text-right p-3 text-slate-500 font-medium">Factures</th>
                      <th className="text-right p-3 text-slate-500 font-medium">Montant</th>
                      <th className="text-right p-3 text-slate-500 font-medium">Recouvre</th>
                      <th className="text-right p-3 text-slate-500 font-medium">Reste</th>
                      <th className="text-right p-3 text-slate-500 font-medium">Taux</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankingData.rankings.map((r) => (
                      <tr key={r.rank} className={`border-b border-slate-100 ${r.is_me ? "bg-sky-50 border-sky-200" : "hover:bg-slate-50"}`}>
                        <td className="p-3">
                          {r.rank <= 3 ? (
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${r.rank === 1 ? "bg-amber-400" : r.rank === 2 ? "bg-slate-400" : "bg-amber-600"}`}>{r.rank}</span>
                          ) : <span className="text-slate-500 font-medium">{r.rank}</span>}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {r.is_me && <Star className="w-3.5 h-3.5 text-sky-500 fill-sky-500" />}
                            <span className={`font-medium ${r.is_me ? "text-sky-700" : "text-slate-700"}`}>{r.name}</span>
                            {r.is_me && <span className="text-[9px] bg-sky-100 text-sky-600 px-1.5 py-0.5 rounded-full">Vous</span>}
                          </div>
                        </td>
                        <td className="p-3 text-right text-slate-600">{r.total_invoices}</td>
                        <td className="p-3 text-right text-slate-800 font-medium">{formatFCFA(r.total_amount)}</td>
                        <td className="p-3 text-right text-emerald-600 font-medium">{formatFCFA(r.total_paid)}</td>
                        <td className="p-3 text-right text-amber-600">{formatFCFA(r.pending)}</td>
                        <td className="p-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.recovery_rate >= 50 ? "bg-emerald-100 text-emerald-700" : r.recovery_rate >= 25 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                            {r.recovery_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {tab === "performance" && (
          <div className="space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <p className="text-[10px] text-slate-500 uppercase mb-2">Mes factures</p>
                <p className="text-2xl font-bold text-slate-800">{my.total_invoices || 0}</p>
                <p className="text-xs text-slate-500 mt-1">{formatFCFA(my.total_amount)}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <p className="text-[10px] text-slate-500 uppercase mb-2">Mon taux de recouvrement</p>
                <p className="text-2xl font-bold text-emerald-600">{my.recovery_rate || 0}%</p>
                <p className="text-xs text-slate-500 mt-1">{formatFCFA(my.total_paid)} recouvrés</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <p className="text-[10px] text-slate-500 uppercase mb-2">Classement</p>
                <p className="text-2xl font-bold text-sky-600">#{my.rank || "-"}</p>
                <p className="text-xs text-slate-500 mt-1">sur {my.pharmacy_count || 0} pharmacie(s)</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <p className="text-[10px] text-slate-500 uppercase mb-2">Percentile</p>
                <p className="text-2xl font-bold text-amber-600">{my.percentile || 0}%</p>
                <p className="text-xs text-slate-500 mt-1">Top {100 - (my.percentile || 0)}%</p>
              </div>
            </div>

            {/* Benchmark */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-sky-500" /> Comparaison avec le marché</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Votre taux</p>
                  <p className="text-3xl font-bold text-emerald-600">{my.recovery_rate || 0}%</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Moyenne marché</p>
                  <p className="text-3xl font-bold text-slate-600">{market.avg_recovery_rate || 0}%</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Écart</p>
                  <div className="flex items-center justify-center gap-2">
                    {diff >= 0 ? <TrendingUp className="w-5 h-5 text-emerald-500" /> : <TrendingDown className="w-5 h-5 text-red-500" />}
                    <p className={`text-3xl font-bold ${diff >= 0 ? "text-emerald-600" : "text-red-600"}`}>{diff >= 0 ? "+" : ""}{diff.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recovery by Type Chart */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800 mb-4">Recouvrements par type de paiement</h3>
              {(data?.recovery_by_type || []).length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.recovery_by_type.map(r => ({ name: r.type || "Non specifie", value: r.total || r.count || 0 }))} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value, percent }) => value > 0 ? `${name} (${(percent * 100).toFixed(0)}%)` : ""}>
                        {data.recovery_by_type.map((r, i) => {
                          const typeColors = { "Especes": "#10B981", "Espèces": "#10B981", "Cheque": "#3B82F6", "Chèque": "#3B82F6", "Virement bancaire": "#8B5CF6", "Mobile Money": "#F59E0B", "Carte bancaire": "#06B6D4" };
                          return <Cell key={i} fill={typeColors[r.type] || COLORS[i % COLORS.length]} />;
                        })}
                      </Pie>
                      <Tooltip formatter={(v) => formatFCFA(v)} contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", color: "#334155" }} />
                      <Legend wrapperStyle={{ color: "#64748b", fontSize: "12px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                  <div className="text-center">
                    <p>Aucune donnee de recouvrement disponible</p>
                    <p className="text-xs mt-1">Les donnees apparaitront apres les premiers paiements enregistres</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Geographic Tab - Enhanced */}
        {tab === "geographic" && geoData && (
          <div className="space-y-6" data-testid="geographic-tab">
            {/* National KPIs */}
            <div className="bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-200 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2"><Globe className="w-4 h-4 text-sky-500" /> Vue nationale</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Taux recouvrement</p>
                  <p className="text-2xl font-bold text-emerald-600">{geoData.national.recovery_rate}%</p>
                </div>
                <div className="text-center bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Factures</p>
                  <p className="text-2xl font-bold text-slate-800">{geoData.national.invoice_count}</p>
                </div>
                <div className="text-center bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Montant total</p>
                  <p className="text-lg font-bold text-slate-800">{formatFCFA(geoData.national.total_amount)}</p>
                </div>
                <div className="text-center bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                  <p className="text-[10px] text-slate-500 uppercase mb-1">Pharmacies</p>
                  <p className="text-2xl font-bold text-sky-600">{geoData.national.pharmacy_count}</p>
                </div>
              </div>
            </div>

            {/* By Ville - Stacked Bar Chart (Recouvre vs Reste) */}
            {geoData.by_ville?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800 mb-1">Comparaison par ville</h3>
                <p className="text-[10px] text-slate-400 mb-4">Montant facture vs recouvre par ville. {geoData.my_ville && <span className="text-sky-600 font-medium">Votre ville: {geoData.my_ville}</span>}</p>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={geoData.by_ville.slice(0, 10).map(v => ({
                      ...v,
                      remaining: v.total_amount - v.paid_amount,
                      is_mine: v.name === geoData.my_ville,
                    }))} layout="vertical" margin={{ left: 10, right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={formatShort} />
                      <YAxis type="category" dataKey="name" width={110} tick={({ x, y, payload }) => {
                        const isMine = payload.value === geoData.my_ville;
                        return (
                          <text x={x} y={y} dy={4} textAnchor="end" fill={isMine ? "#0369a1" : "#334155"} fontSize={11} fontWeight={isMine ? 700 : 400}>
                            {isMine ? `★ ${payload.value}` : payload.value}
                          </text>
                        );
                      }} />
                      <Tooltip content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg text-xs">
                            <p className="font-bold text-slate-800 mb-1">{d.is_mine ? `★ ${d.name} (votre ville)` : d.name}</p>
                            <p className="text-emerald-600">Recouvre: {formatFCFA(d.paid_amount)}</p>
                            <p className="text-amber-600">Reste: {formatFCFA(d.remaining)}</p>
                            <p className="text-slate-500">Taux: <span className="font-bold">{d.recovery_rate}%</span></p>
                            <p className="text-slate-400">{d.pharmacy_count} pharmacie(s) - {d.invoice_count} facture(s)</p>
                          </div>
                        );
                      }} />
                      <Bar dataKey="paid_amount" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} name="Recouvre" />
                      <Bar dataKey="remaining" stackId="a" fill="#FBBF24" radius={[0, 4, 4, 0]} name="Reste a recouvrer" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-400">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" /> Recouvre</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400" /> Reste a recouvrer</span>
                  {geoData.my_ville && <span className="flex items-center gap-1"><Star className="w-3 h-3 text-sky-500" /> Votre ville</span>}
                </div>
              </div>
            )}

            {/* By Commune - Stacked Bar Chart */}
            {geoData.by_commune?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800 mb-1">Comparaison par commune</h3>
                <p className="text-[10px] text-slate-400 mb-4">Montant facture vs recouvre par commune. {geoData.my_commune && <span className="text-sky-600 font-medium">Votre commune: {geoData.my_commune}</span>}</p>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={geoData.by_commune.slice(0, 10).map(c => ({
                      ...c,
                      remaining: c.total_amount - c.paid_amount,
                      is_mine: c.name === geoData.my_commune,
                    }))} layout="vertical" margin={{ left: 10, right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={formatShort} />
                      <YAxis type="category" dataKey="name" width={110} tick={({ x, y, payload }) => {
                        const isMine = payload.value === geoData.my_commune;
                        return (
                          <text x={x} y={y} dy={4} textAnchor="end" fill={isMine ? "#0369a1" : "#334155"} fontSize={11} fontWeight={isMine ? 700 : 400}>
                            {isMine ? `★ ${payload.value}` : payload.value}
                          </text>
                        );
                      }} />
                      <Tooltip content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg text-xs">
                            <p className="font-bold text-slate-800 mb-1">{d.is_mine ? `★ ${d.name} (votre commune)` : d.name}</p>
                            <p className="text-emerald-600">Recouvre: {formatFCFA(d.paid_amount)}</p>
                            <p className="text-amber-600">Reste: {formatFCFA(d.remaining)}</p>
                            <p className="text-slate-500">Taux: <span className="font-bold">{d.recovery_rate}%</span></p>
                            <p className="text-slate-400">{d.pharmacy_count} pharmacie(s) - {d.invoice_count} facture(s)</p>
                          </div>
                        );
                      }} />
                      <Bar dataKey="paid_amount" stackId="a" fill="#3B82F6" radius={[0, 0, 0, 0]} name="Recouvre" />
                      <Bar dataKey="remaining" stackId="a" fill="#F97316" radius={[0, 4, 4, 0]} name="Reste a recouvrer" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-400">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500" /> Recouvre</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500" /> Reste a recouvrer</span>
                  {geoData.my_commune && <span className="flex items-center gap-1"><Star className="w-3 h-3 text-sky-500" /> Votre commune</span>}
                </div>
              </div>
            )}

            {/* Taux de recouvrement comparison - Horizontal bar with color coding */}
            <div className="grid lg:grid-cols-2 gap-6">
              {geoData.by_ville?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-800 mb-4">Taux de recouvrement par ville</h3>
                  <div className="space-y-3">
                    {geoData.by_ville.map((v, i) => {
                      const isMine = v.name === geoData.my_ville;
                      const barColor = v.recovery_rate >= 50 ? "bg-emerald-500" : v.recovery_rate >= 25 ? "bg-amber-500" : "bg-red-500";
                      return (
                        <div key={i} className={`p-3 rounded-lg border ${isMine ? "bg-sky-50 border-sky-300" : "bg-slate-50 border-slate-100"}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              {isMine && <Star className="w-3 h-3 text-sky-500 fill-sky-500" />}
                              <span className={`text-xs font-medium ${isMine ? "text-sky-700" : "text-slate-700"}`}>{v.name}</span>
                              <span className="text-[10px] text-slate-400">{v.pharmacy_count} pharma.</span>
                            </div>
                            <span className={`text-sm font-bold ${v.recovery_rate >= 50 ? "text-emerald-600" : v.recovery_rate >= 25 ? "text-amber-600" : "text-red-600"}`}>{v.recovery_rate}%</span>
                          </div>
                          <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(v.recovery_rate, 100)}%` }} />
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-[10px] text-slate-400">{formatFCFA(v.paid_amount)} recouvre</span>
                            <span className="text-[10px] text-slate-400">/ {formatFCFA(v.total_amount)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {geoData.by_commune?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-800 mb-4">Taux de recouvrement par commune</h3>
                  <div className="space-y-3">
                    {geoData.by_commune.map((c, i) => {
                      const isMine = c.name === geoData.my_commune;
                      const barColor = c.recovery_rate >= 50 ? "bg-emerald-500" : c.recovery_rate >= 25 ? "bg-amber-500" : "bg-red-500";
                      return (
                        <div key={i} className={`p-3 rounded-lg border ${isMine ? "bg-sky-50 border-sky-300" : "bg-slate-50 border-slate-100"}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              {isMine && <Star className="w-3 h-3 text-sky-500 fill-sky-500" />}
                              <span className={`text-xs font-medium ${isMine ? "text-sky-700" : "text-slate-700"}`}>{c.name}</span>
                              <span className="text-[10px] text-slate-400">{c.pharmacy_count} pharma.</span>
                            </div>
                            <span className={`text-sm font-bold ${c.recovery_rate >= 50 ? "text-emerald-600" : c.recovery_rate >= 25 ? "text-amber-600" : "text-red-600"}`}>{c.recovery_rate}%</span>
                          </div>
                          <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(c.recovery_rate, 100)}%` }} />
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-[10px] text-slate-400">{formatFCFA(c.paid_amount)} recouvre</span>
                            <span className="text-[10px] text-slate-400">/ {formatFCFA(c.total_amount)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Insurer by Commune */}
            {geoData.insurer_by_commune?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">Assureurs dans votre commune ({geoData.my_commune})</h3>
                <div className="space-y-2">
                  {geoData.insurer_by_commune.map((ins, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{ins.name}</p>
                        <p className="text-[10px] text-slate-500">{ins.count} facture(s) - {formatFCFA(ins.total)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-slate-200 rounded-full h-2">
                          <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${ins.rate}%` }} />
                        </div>
                        <span className="text-sm font-bold text-emerald-600 w-12 text-right">{ins.rate}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Insurers Tab */}
        {tab === "insurers" && (
          <div className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* My Insurers */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2"><Building2 className="w-4 h-4 text-sky-500" /> Mes assureurs</h3>
                {(data?.my_insurers || []).length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6">Aucune donnée</p>
                ) : (
                  <>
                    <div className="h-52 mb-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.my_insurers.slice(0, 8)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                          <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                          <Tooltip formatter={(v) => `${v}%`} contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", color: "#334155" }} />
                          <Bar dataKey="rate" fill="#10B981" radius={[4, 4, 0, 0]} name="Taux recouvrement (%)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                      {data.my_insurers.map((ins, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-slate-50 border border-slate-100 rounded-lg">
                          <div>
                            <p className="text-xs font-medium text-slate-800">{ins.name}</p>
                            <p className="text-[10px] text-slate-500">{ins.count} fact. - {formatFCFA(ins.total)}</p>
                          </div>
                          <span className="text-sm font-bold text-emerald-600">{ins.rate}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Market Insurers */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> Performance marché par assureur</h3>
                {(data?.insurer_performance || []).length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6">Aucune donnée de marché</p>
                ) : (
                  <>
                    <div className="h-52 mb-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.insurer_performance.slice(0, 8)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                          <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                          <Tooltip formatter={(v) => `${v}%`} contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", color: "#334155" }} />
                          <Bar dataKey="market_rate" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Taux marché (%)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                      {data.insurer_performance.map((ins, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-slate-50 border border-slate-100 rounded-lg">
                          <div>
                            <p className="text-xs font-medium text-slate-800">{ins.name}</p>
                            <p className="text-[10px] text-slate-500">{formatFCFA(ins.market_total)}</p>
                          </div>
                          <span className="text-sm font-bold text-sky-600">{ins.market_rate}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </LogLayout>
  );
}


// ---- MONTHLY COMPARATIVE TAB ----
function MonthlyComparativeTab({ data, userRole, sendingEmail, setSendingEmail, selectedMonth, onMonthChange, monthlyLoading }) {
  const curr = data.current || {};
  const prev = data.previous || {};
  const vars = data.variations || {};
  const trend = data.trend || [];
  const availableMonths = data.available_months || [];

  const sendEmails = async () => {
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/log/analytics/send-monthly-email`, {}, { headers: getHeaders() });
      toast.success(`Email recap envoyé a ${res.data.sent} pharmacie(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi email"); }
    finally { setSendingEmail(false); }
  };

  const VariationBadge = ({ value, suffix = "%", inverse = false }) => {
    const isPositive = inverse ? value <= 0 : value >= 0;
    const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
    return (
      <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${isPositive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
        <Icon className="w-3 h-3" /> {value > 0 ? "+" : ""}{value}{suffix}
      </span>
    );
  };

  const CompareCard = ({ label, current, previous, variation, format = "number", inverse = false, color = "slate" }) => {
    const fmtVal = (v) => format === "fcfa" ? formatFCFA(v) : format === "percent" ? `${v}%` : v;
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] text-slate-500 uppercase font-medium">{label}</p>
          {variation !== undefined && <VariationBadge value={variation} inverse={inverse} />}
        </div>
        <p className={`text-xl font-bold ${color === "emerald" ? "text-emerald-600" : color === "amber" ? "text-amber-600" : color === "red" ? "text-red-600" : "text-slate-800"}`}>{fmtVal(current)}</p>
        <p className="text-[10px] text-slate-400 mt-1">Mois précédent: {fmtVal(previous)}</p>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="monthly-comparative">
      {/* Header + Month Selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-sky-500" /> {data.current_month} vs {data.prev_month}</h3>
          <p className="text-[10px] text-slate-500 mt-0.5">Comparaison de vos indicateurs mois par mois</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-slate-500 font-medium">Mois à analyser :</label>
          <select
            value={selectedMonth || data.selected_month_value || ""}
            onChange={(e) => onMonthChange && onMonthChange(e.target.value)}
            disabled={monthlyLoading}
            data-testid="monthly-month-selector"
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs bg-white text-slate-700 focus:border-sky-500 focus:outline-none disabled:opacity-50"
          >
            {availableMonths.length === 0 && data.selected_month_value && (
              <option value={data.selected_month_value}>{data.current_month}</option>
            )}
            {availableMonths.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}{m.is_current ? " (actuel)" : ""}
              </option>
            ))}
          </select>
          {monthlyLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-500" />}
        </div>
      </div>
      <div className="flex items-center justify-end">
        {(userRole === "admin" || userRole === "superadmin") && (
          <Button onClick={sendEmails} disabled={sendingEmail} data-testid="send-monthly-email-btn"
            className="bg-sky-600 hover:bg-sky-500 text-white text-xs gap-2">
            {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Envoyer le recap aux pharmacies
          </Button>
        )}
      </div>

      {/* KPI comparison grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CompareCard label="Factures émises" current={curr.nb_invoices} previous={prev.nb_invoices} variation={vars.invoices} />
        <CompareCard label="Montant facturé" current={curr.total_amount} previous={prev.total_amount} variation={vars.amount} format="fcfa" />
        <CompareCard label="Montant recouvré" current={curr.total_paid} previous={prev.total_paid} variation={vars.paid} format="fcfa" color="emerald" />
        <CompareCard label="Taux de recouvrement" current={curr.recovery_rate} previous={prev.recovery_rate} variation={vars.recovery_rate} format="percent" color="emerald" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CompareCard label="Reste a recouvrer" current={curr.remaining} previous={prev.remaining} format="fcfa" color="amber" />
        <CompareCard label="Factures payées" current={curr.nb_paid} previous={prev.nb_paid} color="emerald" />
        <CompareCard label="Factures rejetées" current={curr.nb_rejected} previous={prev.nb_rejected} variation={vars.rejected} inverse color="red" />
        <CompareCard label="Factures en attente" current={curr.nb_pending} previous={prev.nb_pending} color="amber" />
      </div>

      {/* Treasury insight */}
      {data.monthly_sales > 0 && (
        <div className={`border rounded-xl p-4 ${data.credit_ratio > 50 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-800">Ratio créances / CA mensuel</p>
              <p className="text-[10px] text-slate-500 mt-0.5">CA renseigné: {formatFCFA(data.monthly_sales)} | Créances: {formatFCFA(curr.remaining)}</p>
            </div>
            <p className={`text-2xl font-bold ${data.credit_ratio > 50 ? "text-red-600" : "text-emerald-600"}`}>{data.credit_ratio}%</p>
          </div>
          {data.credit_ratio > 50 && <p className="text-[10px] text-red-600 mt-2">Attention : vos créances dépassent 50% de votre chiffre d'affaires mensuel.</p>}
        </div>
      )}

      {/* 6-month trend chart */}
      {trend.length > 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <h4 className="text-xs font-bold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-sky-500" /> Évolution montants (6 mois)</h4>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="gradAmt" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.2}/><stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/></linearGradient>
                    <linearGradient id="gradPaid" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/><stop offset="95%" stopColor="#10B981" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month_short" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: 12 }} formatter={(v) => formatFCFA(v)} labelStyle={{ color: "#334155" }} />
                  <Area type="monotone" dataKey="amount" stroke="#3B82F6" fill="url(#gradAmt)" strokeWidth={2} name="Facturé" />
                  <Area type="monotone" dataKey="paid" stroke="#10B981" fill="url(#gradPaid)" strokeWidth={2} name="Recouvré" />
                  <Legend wrapperStyle={{ fontSize: "11px", color: "#64748b" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <h4 className="text-xs font-bold text-slate-800 mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-500" /> Taux de recouvrement (6 mois)</h4>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month_short" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 10 }} domain={[0, 100]} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: 12 }} formatter={(v) => `${v}%`} labelStyle={{ color: "#334155" }} />
                  <Bar dataKey="recovery_rate" name="Taux recouvrement" radius={[4, 4, 0, 0]}>
                    {trend.map((entry, i) => (
                      <Cell key={i} fill={entry.recovery_rate >= 50 ? "#10B981" : entry.recovery_rate >= 25 ? "#F59E0B" : "#EF4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Per-insurer comparison */}
      {(curr.insurers || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="p-4 border-b border-slate-200">
            <h4 className="text-xs font-bold text-slate-800 flex items-center gap-2"><Building2 className="w-4 h-4 text-sky-500" /> Détail par assureur — {data.current_month}</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="monthly-insurers-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 text-slate-500 font-medium">Assureur</th>
                  <th className="text-right p-3 text-slate-500 font-medium">Factures</th>
                  <th className="text-right p-3 text-slate-500 font-medium">Montant</th>
                  <th className="text-right p-3 text-slate-500 font-medium">Recouvré</th>
                  <th className="text-right p-3 text-slate-500 font-medium">Taux</th>
                </tr>
              </thead>
              <tbody>
                {curr.insurers.map((ins, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 text-slate-800 font-medium">{ins.name}</td>
                    <td className="p-3 text-right text-slate-600">{ins.count}</td>
                    <td className="p-3 text-right text-slate-800">{formatFCFA(ins.total)}</td>
                    <td className="p-3 text-right text-emerald-600">{formatFCFA(ins.paid)}</td>
                    <td className="p-3 text-right"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${ins.rate >= 50 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{ins.rate}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Auto email info */}
      <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex items-center gap-3">
        <Mail className="w-5 h-5 text-sky-500 flex-shrink-0" />
        <div>
          <p className="text-xs text-slate-800 font-medium">Email récap automatique</p>
          <p className="text-[10px] text-slate-500">Un email récapitulatif est envoyé automatiquement à chaque pharmacie le 1er de chaque mois. Les admins peuvent aussi déclencher l'envoi manuellement.</p>
        </div>
      </div>
    </div>
  );
}
